import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { STATUSES, fmtMoney } from "../lib/constants";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <p className="font-medium">{label || payload[0].name}</p>
      <p className="text-primary">{payload[0].value}</p>
    </div>
  );
};

export default function Analytics() {
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/dashboard/stats").then((r) => r.data) });
  const s = stats || { by_status: {}, by_source: {} };

  const statusData = STATUSES.map((st) => ({ name: st.label, value: s.by_status?.[st.key] || 0, color: st.hex }));
  const sourceData = Object.entries(s.by_source || {}).map(([k, v]) => ({ name: k, value: v }));
  const SOURCE_COLORS = ["#00F0FF", "#6366F1", "#10B981", "#F59E0B", "#8B5CF6", "#F43F5E", "#3B82F6", "#E1306C"];

  return (
    <div data-testid="analytics-page" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground mt-1">Understand what's converting and where deals stall.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Leads", value: s.total_leads ?? 0 },
          { label: "Conversion", value: `${s.conversion_rate ?? 0}%` },
          { label: "Won Value", value: fmtMoney(s.won_value) },
          { label: "Pipeline Value", value: fmtMoney(s.pipeline_value) },
        ].map((m) => (
          <div key={m.label} className="rounded-2xl border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono">{m.label}</p>
            <p className="mt-2 text-2xl font-extrabold font-display">{m.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display font-semibold mb-5">Leads by stage</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={statusData}>
              <XAxis dataKey="name" tick={{ fill: "hsl(215 20% 60%)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "hsl(215 20% 60%)", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(222 25% 17% / 0.5)" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {statusData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display font-semibold mb-5">Lead sources</h3>
          {sourceData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-20 text-center">No source data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3}>
                  {sourceData.map((d, i) => <Cell key={d.name} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            {sourceData.map((d, i) => (
              <span key={d.name} className="flex items-center gap-1.5 text-xs capitalize">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }} />
                {d.name} ({d.value})
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
